import { positionCodeSchema } from '@opti-sea/core';
import { z } from 'zod';
import { daysBetween, isIsoDate, prepPhase, type InspectionPlan } from './prep';
import { percentOf } from './status';

/**
 * Final checks (Task 6b, PLAN §2.12): the company's list for the last day before the inspection,
 * from the owner's document "1. Important checks before inspection". Groups say who ticks and
 * when; some items take a value (a date or a short text); some are linked to RISQ questions
 * flagged C/L.
 */

/** When a group is done. Database enum `risq_final_check_timing`. */
export const FINAL_CHECK_TIMINGS = ['day_before', 'hour_before'] as const;
export type FinalCheckTiming = (typeof FINAL_CHECK_TIMINGS)[number];
export const finalCheckTimingSchema = z.enum(FINAL_CHECK_TIMINGS);

/** Input field of an item. Database enum `risq_final_check_input`. */
export const FINAL_CHECK_INPUT_KINDS = ['date', 'text'] as const;
export type FinalCheckInputKind = (typeof FINAL_CHECK_INPUT_KINDS)[number];
export const finalCheckInputKindSchema = z.enum(FINAL_CHECK_INPUT_KINDS);

/** Longest value of an input field (same limit as the server). */
export const FINAL_CHECK_VALUE_MAX = 200;

const keySchema = z.string().regex(/^[a-z0-9][a-z0-9-]{0,63}$/);
const questionKeySchema = z.string().regex(/^[0-9A-Za-z.]{1,32}$/);

/**
 * The structured copy of the owner's document (`data/source/final-checks.json`), read by the
 * importer and the phone's demo. `questions` are linked; `doubtful_questions` are possible matches
 * for the owner to confirm (shown in the import report, not linked).
 */
export const finalChecksFileSchema = z
  .object({
    source: z.string().min(1),
    notes: z.array(z.string()).default([]),
    groups: z
      .array(
        z.object({
          key: keySchema,
          title: z.string().trim().min(1).max(200),
          timing: finalCheckTimingSchema,
          positions: z.array(positionCodeSchema).min(1).max(16),
          items: z
            .array(
              z.object({
                key: keySchema,
                text: z.string().trim().min(1).max(1000),
                input: z
                  .object({
                    kind: finalCheckInputKindSchema,
                    label: z.string().trim().min(1).max(200),
                  })
                  .optional(),
                questions: z.array(questionKeySchema).default([]),
                doubtful_questions: z.array(questionKeySchema).default([]),
              }),
            )
            .min(1),
        }),
      )
      .min(1),
  })
  .superRefine((file, ctx) => {
    const groups = new Set<string>();
    const items = new Set<string>();
    for (const group of file.groups) {
      if (groups.has(group.key)) {
        ctx.addIssue({ code: 'custom', message: `Group key ${group.key} is used twice` });
      }
      groups.add(group.key);
      for (const item of group.items) {
        if (items.has(item.key)) {
          ctx.addIssue({ code: 'custom', message: `Item key ${item.key} is used twice` });
        }
        items.add(item.key);
      }
    }
  });
export type FinalChecksFile = z.infer<typeof finalChecksFileSchema>;

/** A value typed for an item: a calendar date `YYYY-MM-DD` or 1–200 characters of text. */
export function isValidFinalCheckValue(kind: FinalCheckInputKind, value: string): boolean {
  const trimmed = value.trim();
  if (kind === 'date') {
    return /^\d{4}-\d{2}-\d{2}$/.test(trimmed) && isIsoDate(trimmed);
  }
  return trimmed.length > 0 && trimmed.length <= FINAL_CHECK_VALUE_MAX;
}

/**
 * Final checks are open from the day before the planned inspection until the end of the
 * inspection day (the C/L phase, PLAN §2.11).
 */
export function finalChecksOpen(today: string, plan: InspectionPlan | null): boolean {
  return plan !== null && prepPhase(today, plan) === 'final_24h';
}

/** Days until the Final checks open (0 when open or passed). */
export function daysUntilFinalChecks(today: string, plan: InspectionPlan): number {
  return Math.max(0, daysBetween(today, plan.plannedOn) - 1);
}

/** Whether a person ticks a group: one of its positions, or the Master (every group). */
export function canTickFinalGroup(position: string, groupPositions: readonly string[]): boolean {
  return position === 'MASTER' || groupPositions.includes(position);
}

export interface FinalCheckState {
  checked: boolean;
}

export interface FinalChecksSummary {
  total: number;
  done: number;
  percent: number;
}

/** Done = ticked (an item with an input field is ticked when its value is stored). */
export function summarizeFinalChecks(
  marks: readonly (FinalCheckState | null | undefined)[],
): FinalChecksSummary {
  const done = marks.filter((m) => m?.checked === true).length;
  return { total: marks.length, done, percent: percentOf(done, marks.length) };
}
